import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Examen blanc commencé, jamais rendu, dont le temps est écoulé (copie gardée dans le navigateur).
 * Le reprendre l'enverrait aussitôt avec les réponses laissées : on laisse l'élève choisir entre
 * rendre cette copie telle quelle et l'effacer, au lieu de la rendre à sa place.
 */
interface ExpiredExamChoiceProps {
    examTitle: string;
    /** ISO, l'heure de début gardée avec la copie. */
    startedAt: string;
    answeredCount: number;
    questionCount: number;
    onResume: () => void;
    onAbandon: () => void;
}

function formatStart(startedAt: string): string | null {
    const date = new Date(startedAt);
    if (Number.isNaN(date.getTime())) return null;
    const day = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `le ${day} à ${time}`;
}

const ExpiredExamChoice: React.FC<ExpiredExamChoiceProps> = ({ examTitle, startedAt, answeredCount, questionCount, onResume, onAbandon }) => {
    const when = formatStart(startedAt);
    return (
        <div
            role="alertdialog"
            aria-labelledby="expired-exam-title"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border-2 border-amber-400 dark:border-amber-600"
        >
            <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                <h2 id="expired-exam-title" className="text-lg font-bold text-gray-900 dark:text-white">
                    Examen blanc non rendu
                </h2>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-2">
                Vous avez commencé « {examTitle} »{when ? ` ${when}` : ''} sans rendre votre copie. Le temps de
                l'examen est écoulé : vous ne pouvez plus modifier vos réponses.
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
                {answeredCount} question{answeredCount > 1 ? 's' : ''} sur {questionCount} {answeredCount > 1 ? 'ont' : 'a'} une réponse.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={onResume}
                        className="px-4 py-2 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        Reprendre
                    </button>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Votre copie est rendue telle quelle et notée. Elle compte comme une tentative.
                    </p>
                </div>
                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={onAbandon}
                        className="px-4 py-2 rounded-lg font-medium border-2 border-red-500 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-300 dark:hover:bg-red-900/30"
                    >
                        Abandonner
                    </button>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        La copie est effacée sans être notée et aucune tentative n'est enregistrée. Vous pourrez
                        recommencer un examen complet.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ExpiredExamChoice;
